/**
 * use-sequences.ts — CRUD hooks pour les séquences automatisées
 *
 * Tables : sequences, sequence_enrollments
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTeamId } from "@/hooks/use-team-id";
import { useToast } from "@/hooks/use-toast";
import { victoryToast } from "@/lib/coaching-messages";
import { trackEvent } from "@/lib/analytics";
import { invalidateEnrollmentQueries } from "@/hooks/use-campaign-enrollments";
import type { Database, Json } from "@/integrations/supabase/types";

type SequenceRow = Database["public"]["Tables"]["sequences"]["Row"];

export interface SequenceStep {
  template_id: string;
  template_name?: string;
  delay_hours?: number;          // ancien format (retrocompat)
  trigger?: {
    type: 'delay' | 'event_offset' | 'calendar' | 'periodic';
    delay_hours?: number;
    interval_days?: number;
    stop_on_stage_change?: boolean;
  };
}

// ─── Sequences CRUD ──────────────────────────────────────────────────────────

export function useSequences() {
  const { teamId } = useTeamId();

  return useQuery({
    queryKey: ["sequences", teamId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sequences")
        .select("*")
        .eq("team_id", teamId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as SequenceRow[];
    },
    enabled: !!teamId,
  });
}

export function useCreateSequence() {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      name,
      steps,
      target_campaign,
    }: {
      name: string;
      steps: SequenceStep[];
      target_campaign?: string;
    }) => {
      const { data, error } = await supabase
        .from("sequences")
        .insert({
          name,
          team_id: teamId!,
          steps_json: steps as unknown as Json,
          ...(target_campaign ? { target_campaign } : {}),
        })
        .select("*")
        .single();
      if (error) throw error;
      return data as SequenceRow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sequences", teamId] });
      toast(victoryToast("sequence_created"));
    },
    onError: () => {
      toast({
        title: "Erreur",
        description: "Impossible de créer la séquence.",
        variant: "destructive",
      });
    },
  });
}

export function useUpdateSequence() {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      name,
      steps,
      target_campaign,
    }: {
      id: string;
      name: string;
      steps: SequenceStep[];
      target_campaign?: string;
    }) => {
      const { data, error } = await supabase
        .from("sequences")
        .update({
          name,
          steps_json: steps as unknown as Json,
          ...(target_campaign ? { target_campaign } : {}),
        })
        .eq("id", id)
        .select("*")
        .single();
      if (error) throw error;
      return data as SequenceRow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sequences", teamId] });
      toast({ title: "Séquence mise à jour" });
    },
    onError: () => {
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour la séquence.",
        variant: "destructive",
      });
    },
  });
}

export function useDeleteSequence() {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("sequences")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sequences", teamId] });
      toast({ title: "Séquence supprimée" });
    },
    onError: () => {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer la séquence.",
        variant: "destructive",
      });
    },
  });
}

// ─── Enrollments ─────────────────────────────────────────────────────────────

/**
 * Helper bas niveau : inscrit UN contact dans UNE séquence.
 * Partagé par `useEnrollInSequence` (single) et `useBulkEnrollSequence` (bulk)
 * pour garantir la même logique de calcul (firstDelay, next_run_at, initial_stage).
 */
export async function enrollContactInSequence({
  teamId,
  sequenceId,
  contactId,
  opportunityId,
  conversationId,
}: {
  teamId: string;
  sequenceId: string;
  contactId?: string | null;
  opportunityId?: string | null;
  conversationId?: string | null;
}): Promise<void> {
  const { data: seq, error: seqError } = await supabase
    .from("sequences")
    .select("steps_json")
    .eq("id", sequenceId)
    .single();

  if (seqError) throw seqError;

  const steps = (seq.steps_json ?? []) as unknown as SequenceStep[];
  const firstDelay = steps[0]?.trigger?.delay_hours ?? steps[0]?.delay_hours ?? 0;
  const nextRunAt = new Date(Date.now() + firstDelay * 3600 * 1000).toISOString();

  let initialStage: string | null = null;
  if (contactId) {
    const { data: contact } = await supabase
      .from("immo_contacts")
      .select("stage")
      .eq("id", contactId)
      .single();
    initialStage = contact?.stage ?? null;
  }

  const { error } = await supabase
    .from("sequence_enrollments")
    .insert({
      team_id: teamId,
      sequence_id: sequenceId,
      opportunity_id: opportunityId ?? null,
      contact_id: contactId ?? null,
      conversation_id: conversationId ?? null,
      current_step: 0,
      status: "active",
      next_run_at: nextRunAt,
      initial_stage: initialStage,
      trigger_type: steps[0]?.trigger?.type ?? 'delay',
    });

  if (error) throw error;
}

export function useEnrollInSequence() {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      sequenceId,
      opportunityId,
      contactId,
      conversationId,
    }: {
      sequenceId: string;
      opportunityId?: string;
      contactId?: string;
      conversationId?: string;
    }) => {
      await enrollContactInSequence({
        teamId: teamId!,
        sequenceId,
        contactId,
        opportunityId,
        conversationId,
      });
    },
    onSuccess: (_data, variables) => {
      trackEvent("contact_enrolled", {
        sequence_id: variables.sequenceId,
        contact_id: variables.contactId ?? null,
        opportunity_id: variables.opportunityId ?? null,
      });
      invalidateEnrollmentQueries(queryClient, teamId);
      toast(victoryToast("contact_enrolled"));
    },
    onError: (err) => {
      const msg = err instanceof Error && err.message.includes("duplicate")
        ? "Ce contact est déjà inscrit dans cette séquence."
        : "Impossible d'inscrire le contact.";
      toast({
        title: "Erreur",
        description: msg,
        variant: "destructive",
      });
    },
  });
}

export function useSequenceEnrollments(opportunityId?: string) {
  const { teamId } = useTeamId();

  return useQuery({
    queryKey: ["sequence-enrollments", teamId, opportunityId],
    queryFn: async () => {
      let query = supabase
        .from("sequence_enrollments")
        .select("*, sequences(name)")
        .eq("team_id", teamId!);

      if (opportunityId) {
        query = query.eq("opportunity_id", opportunityId);
      }

      const { data, error } = await query.order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!teamId,
  });
}

export function useToggleSequenceActive() {
  const { teamId } = useTeamId();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from("sequences")
        .update({ is_active })
        .eq("id", id);
      if (error) throw error;
      return is_active;
    },
    onSuccess: (activated, variables) => {
      queryClient.invalidateQueries({ queryKey: ["sequences", teamId] });
      if (activated) {
        trackEvent("campaign_started", { sequence_id: variables.id });
        toast(victoryToast("campaign_activated"));
      }
    },
  });
}

// ─── Batch enrollment lookup ────────────────────────────────────────────────

export interface ContactActiveEnrollment {
  enrollmentId: string;
  sequenceId: string;
  sequenceName: string;
  currentStep: number;
  totalSteps: number;
  nextRunAt: string | null;
  status: string;
}

export function useContactActiveEnrollments() {
  const { teamId } = useTeamId();

  return useQuery({
    queryKey: ["contact-active-enrollments", teamId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sequence_enrollments")
        .select(
          "id, contact_id, current_step, status, next_run_at, sequence_id, sequences(name, steps_json)"
        )
        .eq("team_id", teamId!)
        .eq("status", "active")
        .not("contact_id", "is", null);
      if (error) throw error;

      const map = new Map<string, ContactActiveEnrollment>();
      for (const row of data ?? []) {
        if (!row.contact_id || !row.sequences) continue;
        const seq = row.sequences as Record<string, unknown>;
        const stepsJson = seq.steps_json as unknown;
        const totalSteps = Array.isArray(stepsJson) ? stepsJson.length : 0;
        map.set(row.contact_id, {
          enrollmentId: row.id as string,
          sequenceId: row.sequence_id as string,
          sequenceName: seq.name as string,
          currentStep: (row.current_step as number) ?? 0,
          totalSteps,
          nextRunAt: row.next_run_at as string | null,
          status: (row.status as string) ?? "active",
        });
      }
      return map;
    },
    enabled: !!teamId,
  });
}

export function parseSteps(stepsJson: Json): SequenceStep[] {
  if (!Array.isArray(stepsJson)) return [];
  return stepsJson.filter(
    (s): s is SequenceStep =>
      typeof s === "object" &&
      s !== null &&
      "template_id" in s
  );
}
